import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Android from '@mui/icons-material/Android';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import InstallMobileOutlinedIcon from '@mui/icons-material/InstallMobileOutlined';

import { ReactComponent as UpdateIcon } from '../../../assets/icons/update.svg';
import { useAppFile } from '../../../hooks/use-app-file';
import { usePwaInstall } from '../../../hooks/use-pwa-install';
import {
  useGetAppLinkUpdate,
  useGetAppVariables,
} from '../../../redux/api/appConfigApi';
import {
  AppVariableKeys,
  getAppVariableValueInsensitive,
} from '../../../types/interfaces/app-variable';
import AppButton from '../../designe/buttons/app-button/AppButton';
import AppDialog from '../app-dialog/AppDialog';
import { useStyles } from './InstallAppDialog.styles';

interface IInstallAppDialogProps {
  open: boolean;
  onClose: () => void;
}

const InstallAppDialog = ({ open, onClose }: IInstallAppDialogProps) => {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: appLinkData } = useGetAppLinkUpdate();
  const { data: variables } = useGetAppVariables();
  const { downloadFile, isDownloading } = useAppFile(t);
  const { canInstallPwa, promptInstallPwa, isIosBrowser, isPwaInstalled } =
    usePwaInstall();
  const [showIosGuide, setShowIosGuide] = useState(false);

  const apkFileName =
    getAppVariableValueInsensitive(variables, AppVariableKeys.apkFileName) ??
    'SportGround.apk';

  const canShowInstallIconAction = useMemo(
    () => !isPwaInstalled && (canInstallPwa || isIosBrowser),
    [canInstallPwa, isIosBrowser, isPwaInstalled],
  );

  const handlePwaInstall = async () => {
    if (!canInstallPwa) {
      if (isIosBrowser) {
        setShowIosGuide((prev) => !prev);
      }
      return;
    }
    const installed = await promptInstallPwa();
    if (installed) {
      onClose();
    }
  };

  return (
    <AppDialog open={open} onClose={onClose} title={t('installApp.title')}>
      <Box className={classes.dialogContent}>
        <Box className={classes.iconBadge}>
          <UpdateIcon className={classes.topIcon} />
        </Box>
        <Typography variant="h2">{t('installApp.title')}</Typography>
        <Typography variant="text-md" className={classes.description}>
          {t('installApp.description')}
        </Typography>
        <Typography variant="text-sm" className={classes.hint}>
          {t('installApp.resourcesHint')}
        </Typography>
        <Box className={classes.actions}>
          {appLinkData?.link && (
            <AppButton
              variant="contained"
              color="primary"
              fullWidth
              className={classes.actionButton}
              loading={isDownloading}
              icon={<Android className={classes.buttonIcon} />}
              onClick={() =>
                downloadFile(appLinkData.link, apkFileName, {
                  successMessageKey: 'messages.fileDownloaded',
                })
              }>
              {t('installApp.downloadAndroid')}
            </AppButton>
          )}
          {canShowInstallIconAction && (
            <AppButton
              variant="contained"
              color="primary"
              fullWidth
              className={classes.actionButton}
              icon={
                <InstallMobileOutlinedIcon className={classes.buttonIcon} />
              }
              onClick={() => void handlePwaInstall()}>
              {t('installApp.installPwa')}
            </AppButton>
          )}
          {showIosGuide && (
            <Box className={classes.iosGuide}>
              <Typography variant="text-sm-sb">
                {t('installApp.iosGuideTitle')}
              </Typography>
              <Typography variant="text-sm">
                {t('installApp.iosGuideStep1')}
              </Typography>
              <Typography variant="text-sm">
                {t('installApp.iosGuideStep2')}
              </Typography>
            </Box>
          )}
          <AppButton
            variant="outlined"
            color="primary"
            fullWidth
            className={classes.closeButton}
            onClick={onClose}>
            {t('installApp.notNow')}
          </AppButton>
        </Box>
      </Box>
    </AppDialog>
  );
};

export default InstallAppDialog;
